import React from 'react';
import { Link } from 'react-router-dom';

import partners from '../data/partners';
import { site, contact, menu, sisters } from '../data/site';
import useCompanionHref from '../ui/companion';
import { InstagramIcon } from '../ui/icons';

/* The link columns are derived from the menu's own sections rather than
   named here, so a site whose dropdowns are "Solutions / Systems" gets
   the right headings without touching this file. Two columns: the first
   section on its own, everything else folded into the second, which is
   what keeps the footer three columns wide on all four properties. */
const LOOSE = 'More';

function columns(items) {
    const sections = [];

    for (const item of items) {
        const label = item.section || LOOSE;
        const found = sections.find(s => s.label === label);

        if (found) found.items.push(item);
        else sections.push({ label, items: [item] });
    }

    const [first, ...rest] = sections;

    /* The second column is a catch-all, so its heading names only the
       real sections that landed in it. Letting the section-less items
       contribute their own bucket would set it as "Systems & Services &
       More", which is three words to say "the rest". */
    const named = rest.filter(section => section.label !== LOOSE).map(section => section.label);

    return [
        first || { label: 'Links', items: [] },
        {
            label: named.length ? named.join(' & ') : LOOSE,
            items: rest.flatMap(section => section.items)
        }
    ];
}

/* An external destination has to stay an <a>: handing it to <Link> would
   have the router try to match it as a path and render the 404. */
function FooterLink({ item }) {
    const companion = useCompanionHref(item);

    if (item.companion)
        return <a href={companion}>{item.name} ↗</a>;

    if (item.external || /^https?:/.test(item.href))
        return <a href={item.href} target="_blank" rel="noopener noreferrer">{item.name} ↗</a>;

    return <Link to={item.href}>{item.name}</Link>;
}

/* Two lanes running in opposite directions. Each is the list doubled,
   which is what lets the -50% keyframe loop seamlessly: at the halfway
   point the track is showing the copy, pixel-identical to the start. */
function PartnerLane({ items, reverse }) {
    const lane = [...items, ...items];

    return (
        <div className={`partners__lane${reverse ? ' partners__lane--reverse' : ''}`}>
            <div className="partners__track">
                {lane.map((partner, i) => (
                    <a
                        key={`${partner.alt}-${i}`}
                        href={partner.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-hidden={i >= items.length ? 'true' : undefined}
                        tabIndex={i >= items.length ? -1 : undefined}
                    >
                        <img
                            src={partner.src}
                            alt={partner.alt}
                            data-tone={partner.tone}
                            loading="lazy"
                            decoding="async"
                        />
                    </a>
                ))}
            </div>
        </div>
    );
}

export default function Footer() {
    const year = new Date().getFullYear();
    const half = Math.ceil(partners.length / 2);

    const [primary, secondary] = columns(menu);

    return (
        <footer className="footer">
            <div className="shell">
                <div className="partners">
                    <span className="partners__label">Our trusted partners</span>
                    <PartnerLane items={partners.slice(0, half)} />
                    <PartnerLane items={partners.slice(half)} reverse />
                </div>

                <div className="footer__top">
                    <div className="footer__brand">
                        <Link to="/" aria-label={`${site.name}, home`}>
                            <img src={site.logo} alt={site.name} width="240" height="60" />
                        </Link>

                        <p className="footer__blurb">{site.blurb}</p>

                        <div className="footer__contact">
                            <a href={contact.phoneHref}>{contact.phone}</a>
                            <a href={`mailto:${contact.email}`}>{contact.email}</a>
                            <a href={contact.addressHref} target="_blank" rel="noopener noreferrer">
                                {contact.address}
                            </a>
                        </div>
                    </div>

                    {[primary, secondary].map(column => (
                        <div key={column.label}>
                            <span className="footer__col-label">{column.label}</span>
                            <nav className="footer__links" aria-label={column.label}>
                                {column.items.map(item => (
                                    <FooterLink key={item.href} item={item} />
                                ))}
                                {column === secondary && <Link to="/#contact">Contact</Link>}
                            </nav>
                        </div>
                    ))}
                </div>

                <ul className="sisters">
                    {sisters.map(company => (
                        <li key={company.id} style={{ '--sister': company.accent }}>
                            <a href={company.url} target="_blank" rel="noopener noreferrer">
                                <span className={`sisters__media${company.badge ? ' sisters__media--badge' : ''}`}>
                                    <img
                                        src={company.logo}
                                        alt={company.badge ? '' : company.name}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    {/* The group's mark carries no type, so the
                                        name is set here instead. The wordmarks
                                        already read their own, and repeating it
                                        under them would say everything twice. */}
                                    {company.badge && (
                                        <span className="sisters__wordmark">{company.name}</span>
                                    )}
                                </span>

                                <span className="sisters__cta">
                                    Visit site <span aria-hidden="true">→</span>
                                </span>
                            </a>
                        </li>
                    ))}
                </ul>

                <hr className="rule" />

                <div className="footer__bottom">
                    <p>© {year} {site.name}. All rights reserved.</p>

                    <nav className="footer__legal" aria-label="Legal and social">
                        <Link to="/privacy-policy">Privacy Policy</Link>
                        <a
                            href={contact.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Instagram"
                        >
                            <InstagramIcon style={{ width: '1rem', height: '1rem', fill: 'currentColor' }} />
                        </a>
                    </nav>
                </div>
            </div>
        </footer>
    );
}
